import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Stack,
  Button,
  Container,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import FilePreview from './FilePreview';
import Iconify from '../../iconify/Iconify';
import { useToast } from '../../../hooks/useToast';
import InvalidFilesAlert from './InvalidFilesAlert';
import { dahsedContainer, filesPreviewBox } from './style';
import { DEFAULTALLOWEDTYPES } from './defaultAllowedTypes';
import { directUpload, formDataUpload } from './uploadModes';

// Constants
const MAX_SIZE = 1024 * 1024 * (process.env.REACT_APP_MAX_FILE_SIZE_IN_MB || 100);
const ALLOWED_TYPES =
  process.env.REACT_APP_ALLOWED_FILE_MIME_TYPES?.split(',').map((type) => type.trim()) ||
  DEFAULTALLOWEDTYPES;

const ALLOWED_EXTENSIONS = process.env.REACT_APP_ALLOWED_CUSTOM_EXTENSIONS?.split(
  ','
).map((extension) => extension.trim()) || ['.cnv'];

const DEFAULT_FILE_ICON = '/assets/images/files/fileDefault.png';

export default function FileInput({
  hideModal,
  onUpload,
  ticketId,
  setFilesList,
  setFormData,
  disabled = false,
  removeAttachements,
}) {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { isSmallScreen } = useResponsiveScreen();

  const [files, setFiles] = useState([]);
  const [valid, setValid] = useState(true);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [fileChosen, setFileChosen] = useState(false);
  const [dropzoneEnabled, setDropzoneEnabled] = useState(true);
  const [attachmentId, setAttachmentId] = useState(null);
  const [uploadMode, setUploadMode] = useState('direct');

  const getFileExtension = useCallback((filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
  }, []);

  const getExpectedMimeTypeFromExtension = (extension) => {
    const extensionToMimeMap = {
      '.jpeg': 'image/jpeg',
      '.jpg': 'image/jpeg',
      '.png': 'image/png',
      '.gif': 'image/gif',
      '.webp': 'image/webp',
      '.svg': 'image/svg+xml',
      '.pdf': 'application/pdf',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.csv': 'text/csv',
      '.txt': 'text/plain',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.ppt': 'application/vnd.ms-powerpoint',
      '.pptx':
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.json': 'application/json',
      '.gz': 'application/gzip',
      '.7z': 'application/x-7z-compressed',
      '.zip': 'application/zip',
      '.rar': 'application/vnd.rar',
      '.cnv': 'application/octet-stream',
    };
    return extensionToMimeMap[extension.toLowerCase()] || null;
  };

  const isFileAllowed = useCallback(
    (file) => {
      const fileExtension = getFileExtension(file.path || file.name);
      if (file.size > MAX_SIZE) {
        return false;
      }
      if (ALLOWED_EXTENSIONS.includes(fileExtension)) {
        return true;
      }
      if (file.type && ALLOWED_TYPES.includes(file.type)) {
        return true;
      }
      const expectedMimeForExtension = getExpectedMimeTypeFromExtension(fileExtension);
      if (expectedMimeForExtension && ALLOWED_TYPES.includes(expectedMimeForExtension)) {
        return true;
      }
      return false;
    },
    [getFileExtension]
  );

  const filePreviews = useMemo(
    () =>
      files.map((file) => {
        const isImage = file.type.startsWith('image/');
        const url = isImage ? URL.createObjectURL(file) : DEFAULT_FILE_ICON;
        return (
          <FilePreview
            key={`${file.name}-${file.size}`}
            file={file}
            url={url}
            isImage={isImage}
          />
        );
      }),
    [files]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (disabled || isUploading) return;

      const validFiles = [];
      const rejectedFiles = [];

      acceptedFiles.forEach((file) => {
        if (isFileAllowed(file)) {
          validFiles.push(file);
        } else {
          rejectedFiles.push(file);
        }
      });

      if (rejectedFiles.length === 0) {
        setValid(true);
        setFiles(validFiles);
        setFileChosen(false);
      } else {
        const rejectedNames = rejectedFiles.map((file) => file.name);
        setInvalidFiles(rejectedNames);
        setValid(false);

        // Still add valid files if there are any
        if (validFiles.length > 0) {
          setFiles(validFiles);
        }
      }
    },
    [isFileAllowed, disabled, isUploading]
  );

  const uploadFiles = useCallback(async () => {
    setIsUploading(true);
    setUploadProgress(0);

    if (uploadMode === 'direct') {
      directUpload({
        files,
        ticketId,
        resetFiles,
        onUpload,
        setUploadProgress,
        setDropzoneEnabled,
        setIsUploading,
        hideModal,
        showToast,
        hideToast,
        t,
      });
    } else {
      formDataUpload({
        files,
        setAttachmentId,
        setFormData,
        setFilesList,
        showToast,
        hideToast,
        setIsUploading,
        setFileChosen,
        t,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, ticketId, t, uploadMode]);

  const resetFiles = useCallback(() => {
    setFiles([]);
    setInvalidFiles([]);
    setValid(true);
    setUploadProgress(0);
    setFileChosen(false);

    if (attachmentId && removeAttachements) {
      removeAttachements(attachmentId);
      if (setFilesList) setFilesList(null);
    }

    setIsUploading(false);
  }, [attachmentId, removeAttachements, setFilesList]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || !dropzoneEnabled || isUploading || fileChosen,
    noClick: isUploading,
    noKeyboard: isUploading,
    multiple: uploadMode === 'direct',
  });

  // Determine which upload mode to use based on props
  useEffect(() => {
    if (setFormData && setFilesList) {
      setUploadMode('formData');
    } else {
      setUploadMode('direct');
    }
  }, [setFormData, setFilesList]);

  return (
    <Stack spacing={2}>
      {isUploading && uploadMode === 'direct' && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Typography variant="body2" color="text.secondary">
              {t('inputs.file.uploading')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {uploadProgress}%
            </Typography>
          </Stack>
        </Box>
      )}

      {!valid && invalidFiles.length > 0 && (
        <InvalidFilesAlert
          invalidFiles={invalidFiles}
          setValid={setValid}
          maxSize={MAX_SIZE}
        />
      )}

      <Container
        sx={{
          ...dahsedContainer,
          cursor: !disabled && dropzoneEnabled && !fileChosen ? 'pointer' : 'default',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          height: isSmallScreen ? 200 : 300,
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />

        {isUploading && uploadMode === 'direct' ? (
          <Stack alignItems="center" justifyContent="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body1">{t('inputs.file.processingFiles')}</Typography>
          </Stack>
        ) : filePreviews.length > 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={2} py={1}>
            <Typography variant="h6" sx={{ fontSize: isSmallScreen && '0.675rem' }}>
              {t('inputs.file.filesPreview')}:
            </Typography>
            <Box
              sx={{
                ...filesPreviewBox,
                maxWidth: isSmallScreen ? 140 : 'auto',
              }}
            >
              {filePreviews}
            </Box>
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            spacing={2}
            justifyContent="center"
            sx={{
              color: disabled || isUploading || fileChosen ? 'text.disabled' : 'inherit',
            }}
          >
            <Iconify
              icon={
                isDragActive ? 'material-symbols:cloud-upload' : 'material-symbols:upload'
              }
              height={isSmallScreen ? 40 : 60}
              width={isSmallScreen ? 40 : 60}
              color={
                isDragActive
                  ? 'primary.main'
                  : disabled || fileChosen
                  ? 'text.disabled'
                  : 'text.primary'
              }
            />
            <Typography variant="h6" sx={{ fontSize: isSmallScreen && '0.675rem' }}>
              {isDragActive ? t('inputs.file.dropHere') : t('inputs.file.dragHere')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ fontSize: isSmallScreen && '0.675rem' }}
            >
              {t('inputs.file.maxSize', { size: MAX_SIZE / 1024 / 1024 })}
            </Typography>
            {!isDragActive && (
              <Button
                variant="outlined"
                size={isSmallScreen ? 'small' : 'medium'}
                startIcon={<Iconify icon="material-symbols:attach-file" />}
                disabled={disabled || isUploading || fileChosen}
                sx={{ fontSize: isSmallScreen && '0.675rem' }}
              >
                {t('buttons.chooseFile')}
              </Button>
            )}
          </Stack>
        )}
      </Container>

      {filePreviews.length > 0 && !isUploading && (
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size={isSmallScreen ? 'small' : 'medium'}
            onClick={uploadFiles}
            startIcon={<Iconify icon="material-symbols:cloud-upload" />}
            disabled={files.length === 0 || disabled || fileChosen}
            sx={{ fontSize: isSmallScreen && '0.675rem' }}
          >
            {t('buttons.upload')}
          </Button>
          <Button
            variant="outlined"
            size={isSmallScreen ? 'small' : 'medium'}
            onClick={resetFiles}
            startIcon={<Iconify icon="material-symbols:refresh" />}
            sx={{ fontSize: isSmallScreen && '0.675rem' }}
          >
            {t('buttons.reset')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

FileInput.propTypes = {
  hideModal: PropTypes.func,
  ticketId: PropTypes.number,
  onUpload: PropTypes.func,
  setFilesList: PropTypes.func,
  setFormData: PropTypes.func,
  disabled: PropTypes.bool,
  removeAttachements: PropTypes.func,
};
